import React, { useEffect, useState, useRef } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from "react-router-dom";
import axios from "axios";
import { Toaster } from "@/components/ui/sonner";

// Configure axios defaults for all requests
axios.defaults.withCredentials = true;
axios.defaults.timeout = 30000;

// Pages
import LandingPage from "@/pages/LandingPage";
import SearchPage from "@/pages/SearchPage";
import ListingDetailPage from "@/pages/ListingDetailPage";
import HostDashboard from "@/pages/HostDashboard";
import DriverDashboard from "@/pages/DriverDashboard";
import CreateListingPage from "@/pages/CreateListingPage";
import AdminDashboard from "@/pages/AdminDashboard";
import SecretAdmin from "@/pages/SecretAdmin";
import ProfilePage from "@/pages/ProfilePage";
import BookingSuccessPage from "@/pages/BookingSuccessPage";
import AuthPage from "@/pages/AuthPage";
import MessagesPage from "@/pages/MessagesPage";
import TermsPage from "@/pages/TermsPage";
import PrivacyPage from "@/pages/PrivacyPage";
import AboutPage from "@/pages/AboutPage";
import ForgotPasswordPage from "@/pages/ForgotPasswordPage";

// Use relative API paths when on a custom domain (production) to avoid stale deployment URLs
const BACKEND_URL = (() => {
  const envUrl = process.env.REACT_APP_BACKEND_URL || '';
  if (typeof window !== 'undefined') {
    const currentHost = window.location.hostname;
    // If we're on a custom domain (not preview/localhost), use same-origin API calls
    if (currentHost && !currentHost.includes('preview.emergentagent.com') && !currentHost.includes('localhost')) {
      return window.location.origin;
    }
  }
  return envUrl;
})();
export const API = `${BACKEND_URL}/api`;

// Auth Context
export const AuthContext = React.createContext(null);

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
const AuthCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash;
    const sessionIdMatch = hash.match(/session_id=([^&]+)/);
    
    if (sessionIdMatch) {
      const sessionId = sessionIdMatch[1];
      
      axios.get(`${API}/auth/session`, {
        params: { session_id: sessionId },
        withCredentials: true
      })
      .then(response => {
        window.history.replaceState({}, document.title, window.location.pathname);
        const user = response.data;
        // Route based on actual user role - admin goes to /admin
        if (user.role === 'admin') {
          navigate('/admin', { replace: true });
        } else if (user.role === 'host') {
          navigate('/host', { replace: true });
        } else {
          navigate('/dashboard', { replace: true });
        }
      })
      .catch(error => {
        console.error('Auth error:', error);
        navigate('/');
      });
    } else {
      navigate('/');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Google OAuth Callback Handler - processes the token from backend redirect
const GoogleCallback = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const params = new URLSearchParams(window.location.search);
    const code = params.get('code');
    const state = params.get('state') || '/dashboard';
    
    if (code) {
      // Redirect to backend callback (it will process and redirect back with token)
      window.location.href = `${API}/auth/google/callback?code=${code}&state=${encodeURIComponent(state)}`;
    } else {
      navigate('/auth');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Completing sign in...</p>
      </div>
    </div>
  );
};

// OAuth Token Handler - receives token from backend and stores it
const OAuthCallbackHandler = () => {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    // Parse token from URL hash
    const hash = window.location.hash.substring(1);
    const params = new URLSearchParams(hash);
    const token = params.get('token');
    const requestedRedirect = params.get('redirect') || '/dashboard';

    if (token) {
      // Store token in localStorage
      localStorage.setItem('session_token', token);
      
      // Clear the URL hash
      window.history.replaceState({}, document.title, window.location.pathname);
      
      // Fetch user data and navigate based on ACTUAL role
      axios.get(`${API}/auth/me`, {
        withCredentials: true,
        headers: { 'Authorization': `Bearer ${token}` }
      })
      .then(response => {
        const user = response.data;
        // Route based on actual user role from API - admin goes to /admin
        let targetRedirect = requestedRedirect;
        if (user.role === 'admin') {
          targetRedirect = '/admin';
        } else if (requestedRedirect === '/dashboard') {
          // Only override /dashboard default, not specific requested paths
          targetRedirect = user.role === 'host' ? '/host' : '/dashboard';
        }
        navigate(targetRedirect, { replace: true });
      })
      .catch(error => {
        console.error('Failed to get user after OAuth:', error);
        navigate('/auth?error=session_failed');
      });
    } else {
      navigate('/auth?error=no_token');
    }
  }, [navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center bg-slate-50">
      <div className="text-center">
        <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
        <p className="text-slate-600">Signing you in...</p>
      </div>
    </div>
  );
};

// Protected Route
const ProtectedRoute = ({ children, requiredRole }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [isAuthenticated, setIsAuthenticated] = useState(null);
  const [user, setUser] = useState(null);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        // Always fetch fresh user data from the API
        const storedToken = localStorage.getItem('session_token');
        const headers = storedToken ? { 'Authorization': `Bearer ${storedToken}` } : {};
        
        const response = await axios.get(`${API}/auth/me`, { 
          withCredentials: true,
          headers 
        });
        setUser(response.data);
        setIsAuthenticated(true);
        
        // Store session token if provided
        if (response.data.session_token) {
          localStorage.setItem('session_token', response.data.session_token);
        }
      } catch (error) {
        console.log('Auth check failed:', error.response?.status);
        // Only remove token and redirect on actual auth failures (401/403)
        if (error.response?.status === 401 || error.response?.status === 403) {
          localStorage.removeItem('session_token');
          setIsAuthenticated(false);
          navigate('/auth');
        } else {
          // For network errors, try to use cached user state or retry
          console.log('Network error during auth check');
          const storedToken = localStorage.getItem('session_token');
          if (storedToken) {
            setIsAuthenticated(true);
          } else {
            setIsAuthenticated(false);
            navigate('/auth');
          }
        }
      }
    };
    
    checkAuth();
  }, [navigate]);

  if (isAuthenticated === null) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return null;
  }

  // Wait for user data to be loaded before checking role
  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-slate-50">
        <div className="w-12 h-12 border-4 border-[#5ebc67] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  // ALWAYS allow caspian.mtanos@gmail.com to access any page
  if (user.email === 'caspian.mtanos@gmail.com') {
    return (
      <AuthContext.Provider value={{ user, setUser }}>
        {children}
      </AuthContext.Provider>
    );
  }

  // Check if user has required role (admin role bypasses all role checks)
  if (requiredRole && user.role !== requiredRole && user.role !== 'admin') {
    return <Navigate to="/dashboard" replace />;
  }

  return (
    <AuthContext.Provider value={{ user, setUser }}>
      {children}
    </AuthContext.Provider>
  );
};

// App Router
function AppRouter() {
  const location = useLocation();

  // Check for session_id in URL hash (from OAuth redirect)
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  // Check for token in URL hash (from Google OAuth)
  if (location.hash?.includes('token=')) {
    return <OAuthCallbackHandler />;
  }

  return (
    <Routes>
      <Route path="/" element={<LandingPage />} />
      <Route path="/auth" element={<AuthPage />} />
      <Route path="/auth/callback" element={<OAuthCallbackHandler />} />
      <Route path="/auth/google/callback" element={<GoogleCallback />} />
      <Route path="/login" element={<AuthPage />} />
      <Route path="/signup" element={<AuthPage />} />
      <Route path="/search" element={<SearchPage />} />
      <Route path="/listing/:id" element={<ListingDetailPage />} />
      <Route
        path="/booking-success"
        element={
          <ProtectedRoute>
            <BookingSuccessPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardRouter />
          </ProtectedRoute>
        }
      />
      <Route
        path="/driver-bookings"
        element={
          <ProtectedRoute>
            <DriverDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/host/*"
        element={
          <ProtectedRoute requiredRole="host">
            <HostDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/create-listing"
        element={
          <ProtectedRoute requiredRole="host">
            <CreateListingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/edit-listing/:id"
        element={
          <ProtectedRoute requiredRole="host">
            <CreateListingPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/admin/*"
        element={
          <ProtectedRoute requiredRole="admin">
            <AdminDashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/profile"
        element={
          <ProtectedRoute>
            <ProfilePage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/messages/:conversationId"
        element={
          <ProtectedRoute>
            <MessagesPage />
          </ProtectedRoute>
        }
      />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/about" element={<AboutPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/manage" element={<SecretAdmin />} />
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

// Dashboard Router - routes based on user role
const DashboardRouter = () => {
  const { user } = React.useContext(AuthContext);
  const location = useLocation();
  
  // Check if user was specifically directed here after making a booking
  const fromBooking = location.state?.fromBooking || location.search?.includes('booking');
  
  // If user came from making a booking, always show driver dashboard
  if (fromBooking) {
    return <DriverDashboard />;
  }
  
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  // Hosts see their host dashboard by default, but can still access driver dashboard
  if (user?.role === 'host') {
    return <Navigate to="/host" replace />;
  }
  return <DriverDashboard />;
};

function App() {
  return (
    <div className="App">
      <BrowserRouter>
        <AppRouter />
      </BrowserRouter>
      <Toaster position="top-right" richColors />
    </div>
  );
}

export default App;
